import type { VoidFunctionComponent } from 'react';
import { icons } from '../../../../../../generic';
import { useRemoveUserFromTeamGroup } from '../../../../../../../hooks/api';
import { useAddToast } from '../../../../../../../contexts/ToastContext';
import { useOnConfirm } from '../../../../../../../contexts/DialogContext';
import { Action, ResourceType, useHasPermission } from '../../../../../../../shared/permissions';
import { useHasPermissionForTeamMembers } from '../../../../../../../helpers/permissions';
import { window } from '../../../../../../../globals';
import SettingsTableRowActions from '../../../../../shared/SettingsTableRowActions';

type Props = {
  teamGroupId: number;
  userId: number;
  isDisabled?: boolean;
};

//  Trying to keep window references outside of the component whenever possible.
const getCurrentUserId = () => window.user?.id;

/**
 * Displays a remove button for a team group member.
 */
const TeamGroupMembersTableRemove: VoidFunctionComponent<Props> = ({ teamGroupId, userId, isDisabled }) => {
  const addToast = useAddToast();
  const onConfirm = useOnConfirm();
  const hasPermission = useHasPermission();
  const hasPermissionForTeamMembers = useHasPermissionForTeamMembers();

  const {
    mutateAsync: removeUserFromTeamGroup,
    isLoading: isRemovingUser,
  } = useRemoveUserFromTeamGroup(teamGroupId, userId);

  const canEditRoles = hasPermission({ resourceType: ResourceType.Role, action: Action.Manage });
  const isCurrentUser = getCurrentUserId() === userId;
  const canRemoveMembers = (!isCurrentUser && canEditRoles) || hasPermissionForTeamMembers(Action.Edit, teamGroupId);

  const handleRemoveUser = () => {
    onConfirm({
      title: 'Are you sure?',
      body: 'Are you sure you want to remove this user from this team group?',
      confirmText: 'Remove',
      onConfirm: async () => {
        try {
          await removeUserFromTeamGroup();
          addToast({
            type: 'success',
            content: 'User removed from team group',
          });
        } catch (error) {
          addToast({
            type: 'error',
            content: 'Error removing user from team group',
          });
        }
      },
    });
  };

  return (
    <SettingsTableRowActions>
      <SettingsTableRowActions.IconButton
        tooltipId={`remove-team-group-member-btn-${teamGroupId}`}
        tooltip={!canRemoveMembers ? `You are not permitted to ${isCurrentUser ? 'remove yourself' : 'remove other members'} from this team group.` : 'Remove Member'}
        icon={icons.trashRegular}
        isDisabled={isRemovingUser || !canRemoveMembers || !!isDisabled}
        onClick={handleRemoveUser}
      />
    </SettingsTableRowActions>
  );
};

export default TeamGroupMembersTableRemove;
